import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useCreatePlot } from '@/hooks/use-plots'
import { getApiErrorMessage } from '@/lib/api'

export interface PlotCreatePageProps {
  plotsBasePath: string
}

export function PlotCreatePage({ plotsBasePath }: PlotCreatePageProps) {
  const navigate = useNavigate()
  const createPlot = useCreatePlot()

  const [newNumber, setNewNumber] = useState('')
  const [plotType, setPlotType] = useState<'residential' | 'special_use'>('residential')
  const [developmentStatus, setDevelopmentStatus] = useState<'developer' | 'owner'>('owner')
  const [section, setSection] = useState('')
  const [mainAvenue, setMainAvenue] = useState('')
  const [courtOrStreet, setCourtOrStreet] = useState('')
  const [notes, setNotes] = useState('')
  const [boreholeMeterNumber, setBoreholeMeterNumber] = useState('')
  const [kplcMeterNumber, setKplcMeterNumber] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      const result = await createPlot.mutateAsync({
        newNumber: newNumber.trim(),
        plotType,
        developmentStatus,
        section: section.trim() || undefined,
        mainAvenue: mainAvenue.trim() || undefined,
        courtOrStreet: courtOrStreet.trim() || undefined,
        notes: notes.trim() || undefined,
        boreholeMeterNumber: boreholeMeterNumber.trim() || undefined,
        kplcMeterNumber: kplcMeterNumber.trim() || undefined,
      })
      navigate(`${plotsBasePath}/${result.data.id}`, { replace: true })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not create plot'))
    }
  }

  return (
    <PageShell>
      <PageHeader
        label="Management"
        title="Add plot"
        description="Create a single plot in the registry."
        action={
          <Link
            to={plotsBasePath}
            className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Back to plots
          </Link>
        }
      />

      <div className="mx-auto w-full max-w-xl">
        <Card className="p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Plot number"
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              required
              hint="Unique identifier, e.g. 322 or SPECIAL-CHURCH"
            />
            <div>
              <label className="mb-1.5 block font-sans text-sm font-medium text-ink">Type</label>
              <select
                value={plotType}
                onChange={(e) => setPlotType(e.target.value as 'residential' | 'special_use')}
                className="flex h-10 w-full rounded border border-input bg-card px-3 font-sans text-sm"
              >
                <option value="residential">Residential</option>
                <option value="special_use">Special use</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block font-sans text-sm font-medium text-ink">
                Development status
              </label>
              <select
                value={developmentStatus}
                onChange={(e) => setDevelopmentStatus(e.target.value as 'developer' | 'owner')}
                className="flex h-10 w-full rounded border border-input bg-card px-3 font-sans text-sm"
              >
                <option value="owner">Owner — not yet built</option>
                <option value="developer">Developer — building or moved in</option>
              </select>
            </div>
            <Input label="Section" value={section} onChange={(e) => setSection(e.target.value)} />
            <Input
              label="Main avenue"
              value={mainAvenue}
              onChange={(e) => setMainAvenue(e.target.value)}
            />
            <Input
              label="Court / street"
              value={courtOrStreet}
              onChange={(e) => setCourtOrStreet(e.target.value)}
            />
            <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <Input
              label="Borehole meter no."
              value={boreholeMeterNumber}
              onChange={(e) => setBoreholeMeterNumber(e.target.value)}
            />
            <Input
              label="KPLC meter no."
              value={kplcMeterNumber}
              onChange={(e) => setKplcMeterNumber(e.target.value)}
            />

            {error && (
              <p className="font-sans text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" loading={createPlot.isPending}>
              Create plot
            </Button>
          </form>
        </Card>
      </div>
    </PageShell>
  )
}
